/**
 * Status do aluno — um vocabulário só.
 *
 * A base tem o mesmo status escrito de várias maneiras, porque entrou por
 * caminhos diferentes: o formulário de "+ Novo Aluno" grava em inglês
 * (`enrolled`), a importação de planilha gravava em português
 * (`matriculado`) e, quando a planilha trazia algo fora da lista, o valor
 * cru ia para o banco — foi assim que apareceu `matriculado(a)`. A tela da
 * turma mostrava esse campo direto, então a mesma lista exibia
 * "transferred" numa linha e "transferido" na seguinte.
 *
 * Aqui tudo isso vira um código só, e a tela sempre lê em português. O que
 * está gravado continua valendo — ninguém precisa recadastrar aluno.
 *
 * Duas perguntas saem daqui, e elas são diferentes:
 *
 * - `ehAlunoMatriculado` — conta como aluno ativo da rede (painéis, totais).
 * - `alunoEstaNaTurma`   — entra na chamada e no diário de hoje.
 *
 * A segunda é mais permissiva de propósito: status que ninguém reconhece
 * mantém o aluno na chamada, porque aluno que some da chamada perde
 * frequência em silêncio. Ver `backend/api/alunos_turma.py`, que é o lado
 * de lá da mesma regra.
 */

import { normalizeText } from './normalizeText';

/** Os status que o sistema oferece, na ordem em que fazem sentido. */
export const STATUS_ALUNO = [
  { value: 'enrolled', label: 'Matriculado', color: 'green' },
  { value: 'vacant', label: 'Vacante (sem turma)', color: 'navy' },
  { value: 'transferred', label: 'Transferido', color: 'yellow' },
  { value: 'completed', label: 'Concluído', color: 'blue' },
  { value: 'inactive', label: 'Desistente', color: 'red' },
];

export const STATUS_ALUNO_PADRAO = 'enrolled';
export const STATUS_VACANTE = 'vacant';

// Toda grafia já vista na base — inglês, português, flexão de gênero e as
// abreviações que a importação aceita — apontando para o código canônico.
const SINONIMOS = {
  enrolled: 'enrolled',
  matriculado: 'enrolled',
  matriculada: 'enrolled',
  ativo: 'enrolled',
  ativa: 'enrolled',
  active: 'enrolled',
  m: 'enrolled',

  vacant: 'vacant',
  vacante: 'vacant',
  'sem turma': 'vacant',

  transferred: 'transferred',
  transferido: 'transferred',
  transferida: 'transferred',
  t: 'transferred',

  completed: 'completed',
  concluido: 'completed',
  concluida: 'completed',
  graduated: 'completed',
  formado: 'completed',
  formada: 'completed',

  inactive: 'inactive',
  inativo: 'inactive',
  inativa: 'inactive',
  dropped: 'inactive',
  desistente: 'inactive',
  abandonado: 'inactive',
  abandonada: 'inactive',
  i: 'inactive',
};

/**
 * Código canônico do status, ou `null` quando o valor não é reconhecido.
 *
 * `matriculado(a)` e `Matriculado (A)` chegam à mesma chave: a flexão entre
 * parênteses é decoração de planilha, não informação.
 */
export function normalizarStatusAluno(valor) {
  if (valor === null || valor === undefined) return null;
  const chave = normalizeText(valor)
    .replace(/\([^)]*\)/g, '')
    .replace(/[^a-z\s]/g, '')
    .trim();
  if (!chave) return null;
  return SINONIMOS[chave] || null;
}

/**
 * O que a tela mostra. Status desconhecido aparece como veio, para a
 * pessoa poder corrigir o cadastro em vez de ver um rótulo inventado.
 */
export function rotuloStatusAluno(valor) {
  const codigo = normalizarStatusAluno(valor);
  if (!codigo) return valor ? String(valor) : '—';
  return STATUS_ALUNO.find((s) => s.value === codigo).label;
}

/** Cor do selo. Status desconhecido fica neutro. */
export function corStatusAluno(valor) {
  const codigo = normalizarStatusAluno(valor);
  if (!codigo) return 'gray';
  return STATUS_ALUNO.find((s) => s.value === codigo).color;
}

/** Classes Tailwind do selo, na paleta que as telas já usam. */
export function classesStatusAluno(valor) {
  switch (corStatusAluno(valor)) {
    case 'green': return 'bg-[#13983D]/20 text-[#13983D]';
    case 'yellow': return 'bg-[#F9B047]/20 text-[#F9B047]';
    case 'red': return 'bg-[#E83A3C]/20 text-[#E83A3C]';
    case 'blue': return 'bg-[#0974BA]/20 text-[#0974BA]';
    case 'navy': return 'bg-[#2E4974]/20 text-[#2E4974] dark:text-[#8FA6CC]';
    default: return 'bg-gray-500/20 text-gray-400';
  }
}

/**
 * O aluno conta como matriculado na rede?
 *
 * É esta a pergunta por trás de "alunos ativos" nos painéis e no total da
 * turma — e é onde o `matriculado` da importação ficava de fora.
 */
export function ehAlunoMatriculado(valor) {
  return normalizarStatusAluno(valor) === 'enrolled';
}

/** O aluno está sem turma, esperando remanejamento. */
export function ehAlunoVacante(valor) {
  return normalizarStatusAluno(valor) === STATUS_VACANTE;
}

// Status que significam "este aluno não está mais nesta turma".
const FORA_DA_TURMA = new Set(['vacant', 'transferred', 'completed', 'inactive']);

/**
 * O aluno entra na chamada e no diário desta turma?
 *
 * Note a direção: perguntamos quem SAIU, e não quem está `enrolled`. Status
 * desconhecido (ou vazio) mantém o aluno na chamada — aluno a mais na lista
 * incomoda, aluno a menos apaga registro sem ninguém ver. Espelha
 * `backend/api/alunos_turma.py`.
 */
export function alunoEstaNaTurma(aluno) {
  const codigo = normalizarStatusAluno(aluno?.status);
  return !codigo || !FORA_DA_TURMA.has(codigo);
}
